import type { ParsedStatement, Transaction } from './types';
import { categorize } from './categorize';

const DATE_FORMATS = [
  // YYYY-MM-DD
  /^(\d{4})-(\d{2})-(\d{2})$/,
  // DD/MM/YYYY
  /^(\d{2})\/(\d{2})\/(\d{4})$/,
  // DD-MM-YYYY
  /^(\d{2})-(\d{2})-(\d{4})$/,
];

function normaliseDate(s: string): string | null {
  const t = s.trim();
  for (const re of DATE_FORMATS) {
    const m = re.exec(t);
    if (m) {
      if (re === DATE_FORMATS[0]) return `${m[1]}-${m[2]}-${m[3]}`;
      return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
  }
  return null;
}

function parseMoney(raw: string | undefined): number | null {
  if (raw === undefined || raw === null) return null;
  let s = String(raw).replace(/["\s]/g, '');
  if (!s) return null;
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) { negative = true; s = s.slice(1, -1); }
  if (s.startsWith('-')) { negative = true; s = s.slice(1); }
  if (s.includes(',') && s.includes('.')) s = s.replace(/,/g, '');
  else if (s.includes(',')) s = s.replace(',', '.');
  const n = Number(s);
  if (!isFinite(n)) return null;
  return negative ? -n : n;
}

function findCol(headers: string[], candidates: string[]): number {
  const lower = headers.map(h => h.toLowerCase().trim());
  for (const c of candidates) {
    const idx = lower.indexOf(c);
    if (idx >= 0) return idx;
  }
  return -1;
}

/**
 * Parse any SA-bank-style CSV with a date/description/amount columns.
 * Handles: Date+Description+Amount, Date+Description+Debit+Credit, etc.
 */
export function parseGenericCsv(rows: string[][]): ParsedStatement {
  const warnings: string[] = [];
  if (rows.length === 0) {
    return { bank: 'unknown', account: null, openingBalance: null, closingBalance: null,
             transactions: [], warnings: ['Empty file'], balanceMismatches: [] };
  }
  const headers = rows[0];
  const data = rows.slice(1);

  const dateIdx   = findCol(headers, ['date', 'transaction date', 'posted date', 'posting date']);
  const descIdx   = findCol(headers, ['description', 'details', 'narrative', 'memo', 'transaction']);
  const amountIdx = findCol(headers, ['amount', 'value', 'rand amount']);
  const debitIdx  = findCol(headers, ['debit', 'money out', 'withdrawal', 'payments']);
  const creditIdx = findCol(headers, ['credit', 'money in', 'deposit', 'receipts']);
  const balanceIdx = findCol(headers, ['balance', 'running balance']);

  if (dateIdx < 0 || descIdx < 0 || (amountIdx < 0 && debitIdx < 0 && creditIdx < 0)) {
    warnings.push(`CSV header missing required columns. Got: ${headers.join(', ')}`);
    return { bank: 'unknown', account: null, openingBalance: null, closingBalance: null,
             transactions: [], warnings, balanceMismatches: [] };
  }

  const transactions: Transaction[] = [];
  const balanceMismatches: number[] = [];
  let prevBalance: number | null = null;
  let firstBalance: number | null = null;
  let lastBalance: number | null = null;

  for (const r of data) {
    const rawDate = r[dateIdx];
    const date = normaliseDate(rawDate || '');
    if (!date) { warnings.push(`Skipped row: bad date "${rawDate}"`); continue; }
    const description = (r[descIdx] || '').trim();
    let amount: number | null = null;
    if (amountIdx >= 0) {
      amount = parseMoney(r[amountIdx]);
    } else {
      const debit  = debitIdx  >= 0 ? parseMoney(r[debitIdx])  : 0;
      const credit = creditIdx >= 0 ? parseMoney(r[creditIdx]) : 0;
      amount = (credit || 0) - (debit || 0);
      if (amount === 0) amount = null;
    }
    if (amount === null) { warnings.push(`Skipped row: no amount`); continue; }
    const balance = balanceIdx >= 0 ? parseMoney(r[balanceIdx]) : null;
    if (balance !== null) {
      if (firstBalance === null) firstBalance = balance;
      lastBalance = balance;
      if (prevBalance !== null) {
        const expected = Math.round((prevBalance + amount) * 100) / 100;
        if (Math.abs(expected - balance) > 0.01) balanceMismatches.push(transactions.length);
      }
      prevBalance = balance;
    }
    transactions.push({ date, description, amount, balance, category: categorize(description) });
  }

  return {
    bank: 'unknown',
    account: null,
    openingBalance: firstBalance,
    closingBalance: lastBalance,
    transactions,
    warnings,
    balanceMismatches,
  };
}
